import React, { ReactNode, useEffect, useRef, useState } from 'react'

import {
  Color,
  Text,
  FontWeight,
  withCitrusTheme,
  DisplayPosition,
  Button,
  Box,
  Display,
  JustifyContent,
  TextAlign,
  AlignItems,
  ZIndex,
} from '@gousto-internal/citrus-react'

import { Recipe, RecipeUUID } from '@library/menu-service'
import {
  IngredientSlotValue,
  MenuRecipeOption,
} from '@library/menu-service/queries/menuApiMenuRecipe/types'

import { CrossOut } from './CrossOut'
import { Tooltip } from './Tooltip'

const IngredientSelectedStates = {
  Selected: 'selected',
  Selectable: 'selectable',
  NotAvailable: 'not-available',
  SoldOut: 'sold-out',
} as const

type IngredientSelectedState =
  typeof IngredientSelectedStates[keyof typeof IngredientSelectedStates]

const PillButton = ({
  state,
  children,
  onClick,
}: {
  state: IngredientSelectedState
  children: ReactNode
  onClick: () => void
}) => {
  const isSelected = state === IngredientSelectedStates.Selected
  const isDisabled =
    state === IngredientSelectedStates.NotAvailable || state === IngredientSelectedStates.SoldOut
  const backgroundColor = isSelected ? Color.Secondary_50 : Color.NeutralGrey_50
  const borderColor = isSelected ? Color.Secondary_400 : Color.NeutralGrey_200

  const Component = withCitrusTheme(Button, (theme) => ({
    width: '100%',
    height: '40px',
    fontSize: '14px',
    color: isDisabled ? theme.color(Color.ColdGrey_500) : theme.color(Color.ColdGrey_800),
    backgroundColor: theme.color(backgroundColor),
    borderColor: theme.color(borderColor),
    borderRadius: '100px',
    borderWidth: '2px',
    position: DisplayPosition.Relative,
    '&:hover': {
      backgroundColor: isSelected
        ? theme.color(Color.Secondary_400)
        : theme.color(Color.NeutralGrey_200),
    },
  }))

  return <Component onClick={onClick}>{children}</Component>
}

const getNextRecipeOption = (
  fullRecipeOptions: Recipe[],
  recipeUuid: RecipeUUID,
  options: MenuRecipeOption[],
  ingredientValueId: string,
  slotId: string,
) => {
  const currentRecipeOption = options?.find((option) => option.item_id === recipeUuid)

  const slotValuesIfIngredientSelected = {
    ...(currentRecipeOption?.ingredient_slot_values || {}),
    [slotId]: ingredientValueId,
  }

  const nextRecipeOption = options?.find((option) => {
    const slotValues = option.ingredient_slot_values || {}

    const areSlotValuesEqual =
      JSON.stringify(Object.entries(slotValues).sort()) ===
      JSON.stringify(Object.entries(slotValuesIfIngredientSelected).sort())

    return areSlotValuesEqual
  })

  const isOffered = fullRecipeOptions.some((recipe) => recipe.id === nextRecipeOption?.item_id)

  return isOffered ? nextRecipeOption : null
}

const getIngredientPillState = (
  fullRecipeOptions: Recipe[],
  recipeUuid: RecipeUUID,
  options: MenuRecipeOption[],
  value: IngredientSlotValue,
  slotId: string,
): IngredientSelectedState => {
  const currentRecipeOption = options?.find((option) => option.item_id === recipeUuid)
  const isIngredientSlotInCurrentRecipe =
    currentRecipeOption?.ingredient_slot_values?.[slotId] === value.id

  if (isIngredientSlotInCurrentRecipe) {
    return IngredientSelectedStates.Selected
  }

  const nextRecipeOption = getNextRecipeOption(
    fullRecipeOptions,
    recipeUuid,
    options,
    value.id,
    slotId,
  )

  if (!nextRecipeOption) {
    return IngredientSelectedStates.NotAvailable
  }

  if (nextRecipeOption && nextRecipeOption.is_available) {
    return IngredientSelectedStates.Selectable
  }

  return IngredientSelectedStates.SoldOut
}

type IngredientPillProps = {
  slotId: string
  recipeUuid: RecipeUUID
  options?: MenuRecipeOption[]
  optionsMenuRecipes: Recipe[]
  slotValue: IngredientSlotValue
  onChangeSelection: (id: string) => void
}

export const IngredientPill = ({
  recipeUuid,
  options = [],
  slotValue,
  slotId,
  optionsMenuRecipes,
  onChangeSelection,
}: IngredientPillProps) => {
  const [showTooltip, setShowTooltip] = useState(false)

  const state = getIngredientPillState(optionsMenuRecipes, recipeUuid, options, slotValue, slotId)

  const isSoldOut = state === IngredientSelectedStates.SoldOut
  const isNotAvailable = state === IngredientSelectedStates.NotAvailable

  const onChange = (ingredientValueId: string) => {
    if (isNotAvailable) {
      setShowTooltip(true)

      return
    }

    const recipeOption = getNextRecipeOption(
      optionsMenuRecipes,
      recipeUuid,
      options,
      ingredientValueId,
      slotId,
    )

    if (recipeOption) {
      onChangeSelection(recipeOption?.item_id)
    }
  }

  let tooltipText = ''

  if (isNotAvailable) {
    tooltipText = 'Sorry, this isn’t available with the other options you’ve selected'
  }

  const tooltipRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target as Node)) {
        setShowTooltip(false)
      }
    }

    if (showTooltip) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [showTooltip])

  return (
    <Box position={DisplayPosition.Relative}>
      <PillButton state={state} onClick={() => onChange(slotValue.id)}>
        <Box
          width="100%"
          display={Display.Flex}
          justifyContent={JustifyContent.Center}
          alignItems={AlignItems.Center}
          position={DisplayPosition.Absolute}
          top="0px"
          right="0px"
          bottom="0px"
          left="0px"
        >
          <Box width={['94%', '80%']} height="99%">
            {(isSoldOut || isNotAvailable) && <CrossOut />}
          </Box>
        </Box>
        <Text
          size={1}
          fontWeight={FontWeight.Bold}
          textAlign={TextAlign.Center}
          style={{ whiteSpace: 'nowrap' }}
        >
          {slotValue.name}
        </Text>
      </PillButton>
      {showTooltip && (
        <Box
          ref={tooltipRef}
          position={DisplayPosition.Absolute}
          top="100%"
          left="50%"
          zIndex={ZIndex.Top}
          style={{ transform: 'translateX(-50%)', marginTop: '15px' }}
        >
          <Tooltip text={tooltipText} onClose={() => setShowTooltip(false)} />
        </Box>
      )}
    </Box>
  )
}
